import { useState } from 'react';
import {
  Container, Row, Col, Card, Form, Button,
  Alert, Spinner, Badge, Modal,
} from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { setCredentials } from '../slices/authSlice';
import {
  useUpdateMyRecordMutation,
  useSendNewEmailVerificationMutation,
  useConfirmEmailChangeMutation,
  useSendPasswordResetOtpMutation,
  useChangePasswordWithOtpMutation,
  useRaiseSchemeMembershipQueryMutation,
} from '../slices/spsApiSlices';

const CIVIL_OPTIONS = [
  '', 'Single', 'Married', 'Civil Partnered', 'Co-Habiting',
  'Separated', 'Divorced', 'Widowed', 'Surviving Civil Partner',
  'A former Civil Partner', 'Prefer not to say', 'Other',
];

const SECTION_LABEL = {
  fontFamily: 'inherit', fontSize: '0.72rem', fontWeight: 700,
  letterSpacing: '0.08em', textTransform: 'uppercase',
  color: '#1a5276', marginBottom: '0.6rem',
};

function passwordStrength(pw) {
  if (!pw) return null;
  let score = 0;
  if (pw.length >= 8)            score++;
  if (/[A-Z]/.test(pw))         score++;
  if (/[0-9]/.test(pw))         score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { label: 'Weak',   variant: 'danger',  width: '25%' };
  if (score === 2) return { label: 'Fair',   variant: 'warning', width: '50%' };
  if (score === 3) return { label: 'Good',   variant: 'info',    width: '75%' };
  return             { label: 'Strong', variant: 'success', width: '100%' };
}

const BLANK_FORM = {
  employeeName: '', addressLine1: '', addressLine2: '',
  addressLine3: '', addressLine4: '', civilStatus: '', dateOfBirth: '',
};

export default function SpsProfileScreen() {
  const dispatch     = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);

  const userEmail = userInfo?.email     || '';
  const ppsNumber = userInfo?.ppsNumber || '';

  const [updateMyRecord,             { isLoading: saving }]         = useUpdateMyRecordMutation();
  const [sendVerification,           { isLoading: sendingCode }]    = useSendNewEmailVerificationMutation();
  const [confirmEmailChange,         { isLoading: confirming }]     = useConfirmEmailChangeMutation();
  const [sendPwOtp,                  { isLoading: sendingPwOtp }]   = useSendPasswordResetOtpMutation();
  const [changePasswordWithOtp,      { isLoading: changingPw }]     = useChangePasswordWithOtpMutation();
  const [raiseSchemeMembershipQuery, { isLoading: raisingQuery }]   = useRaiseSchemeMembershipQueryMutation();

  // ── Details form ──────────────────────────────────────────────────────────
  const [form,     setForm]     = useState(BLANK_FORM);
  const [feedback, setFeedback] = useState(null);
  const upd = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  // ── Scheme membership query — local toggle, saved on form submit ──────────
  const [schemeQueryRaised, setSchemeQueryRaised] = useState(false);

  // ── Save: sends field updates + scheme query state together ───────────────
  const handleSave = async (e) => {
    e.preventDefault();
    setFeedback(null);

    const fieldPayload = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );

    const hasFieldChanges = Object.keys(fieldPayload).length > 0;

    // Run both mutations concurrently — only call each if relevant
    const tasks = [];

    if (hasFieldChanges) {
      tasks.push(updateMyRecord(fieldPayload).unwrap());
    }

    // Always persist the current toggle state so the pensions office is notified
    // if the user raises a query even without changing any other field.
    tasks.push(raiseSchemeMembershipQuery({ raise: schemeQueryRaised }).unwrap());

    if (tasks.length === 1 && !hasFieldChanges) {
      // Only the scheme query — that's a valid save
    } else if (!hasFieldChanges) {
      // Neither changed — warn but still allow scheme query to go through
      // (tasks still has the query mutation)
    }

    try {
      await Promise.all(tasks);
      setFeedback({
        type: 'success',
        msg: hasFieldChanges
          ? 'Your details have been saved. The pensions office has been notified of the changes.'
          : schemeQueryRaised
            ? 'Your scheme membership query has been raised. The pensions office will be in touch.'
            : 'Your scheme membership query status has been updated.',
      });
      setForm(BLANK_FORM);
    } catch (err) {
      setFeedback({
        type: 'danger',
        msg: err?.data?.message || 'Could not save changes — please try again or contact the pensions office.',
      });
    }
  };

  // ── Email change modal ────────────────────────────────────────────────────
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail,       setNewEmail]       = useState('');
  const [confirmEmail,   setConfirmEmail]   = useState('');
  const [emailStep,      setEmailStep]      = useState('input');
  const [otp,            setOtp]            = useState('');
  const [emailFeedback,  setEmailFeedback]  = useState(null);

  const openEmailModal = () => {
    setNewEmail(''); setConfirmEmail(''); setOtp('');
    setEmailStep('input'); setEmailFeedback(null); setShowEmailModal(true);
  };

  const handleSendCode = async () => {
    setEmailFeedback(null);
    const trimmed = newEmail.toLowerCase().trim();
    if (!trimmed) { setEmailFeedback({ type: 'danger', msg: 'Please enter a new email address.' }); return; }
    if (trimmed !== confirmEmail.toLowerCase().trim()) { setEmailFeedback({ type: 'danger', msg: 'The email addresses do not match — please check and try again.' }); return; }
    if (trimmed === userEmail.toLowerCase()) { setEmailFeedback({ type: 'warning', msg: 'That is already your current email address.' }); return; }
    try {
      await sendVerification({ newEmail: trimmed }).unwrap();
      setEmailStep('verify');
      setEmailFeedback({ type: 'success', msg: `A 6-digit code has been sent to ${trimmed}. Check your inbox and enter it below.` });
    } catch (err) {
      setEmailFeedback({ type: 'danger', msg: err?.data?.message || 'Could not send the verification code — please try again.' });
    }
  };

  const handleConfirmEmail = async () => {
    setEmailFeedback(null);
    if (!otp.trim() || otp.trim().length < 6) { setEmailFeedback({ type: 'danger', msg: 'Please enter the 6-digit code.' }); return; }
    try {
      await confirmEmailChange({ newEmail: newEmail.toLowerCase().trim(), otp: otp.trim() }).unwrap();
      dispatch(setCredentials({ ...userInfo, email: newEmail.toLowerCase().trim() }));
      setShowEmailModal(false);
      setFeedback({ type: 'success', msg: `Email address updated to ${newEmail.trim()}. The pensions office has been notified.` });
    } catch (err) {
      setEmailFeedback({ type: 'danger', msg: err?.data?.message || 'Verification failed — check the code and try again.' });
    }
  };

  // ── Password change modal ─────────────────────────────────────────────────
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwStep,      setPwStep]      = useState('send');
  const [pwOtp,       setPwOtp]       = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [showNewPw,   setShowNewPw]   = useState(false);
  const [pwFeedback,  setPwFeedback]  = useState(null);

  const openPwModal = () => {
    setPwStep('send'); setPwOtp(''); setNewPassword(''); setConfirmPw('');
    setShowNewPw(false); setPwFeedback(null); setShowPwModal(true);
  };

  const handleSendPwOtp = async () => {
    setPwFeedback(null);
    try {
      await sendPwOtp().unwrap();
      setPwStep('verify');
      setPwFeedback({ type: 'success', msg: `A 6-digit code has been sent to ${userEmail}. Enter it below along with your new password.` });
    } catch (err) {
      setPwFeedback({ type: 'danger', msg: err?.data?.message || 'Could not send the code — please try again.' });
    }
  };

  const handleChangePassword = async () => {
    setPwFeedback(null);
    if (pwOtp.length < 6)          { setPwFeedback({ type: 'danger', msg: 'Please enter the 6-digit code.' }); return; }
    if (!newPassword)              { setPwFeedback({ type: 'danger', msg: 'Please enter a new password.' }); return; }
    if (newPassword !== confirmPw) { setPwFeedback({ type: 'danger', msg: 'Passwords do not match — please check and try again.' }); return; }
    if (newPassword.length < 8)    { setPwFeedback({ type: 'danger', msg: 'Password must be at least 8 characters.' }); return; }
    try {
      await changePasswordWithOtp({ otp: pwOtp, newPassword }).unwrap();
      setShowPwModal(false);
      setFeedback({ type: 'success', msg: 'Password updated successfully.' });
    } catch (err) {
      setPwFeedback({ type: 'danger', msg: err?.data?.message || 'Could not update password — check the code and try again.' });
      setPwOtp('');
    }
  };

  const pwStrength = passwordStrength(newPassword);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="py-4" style={{ maxWidth: 860 }}>

      <Row className="align-items-start mb-4">
        <Col>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1a5276', marginBottom: '0.3rem' }}>
            Single Public Service Pension Scheme
          </div>
          <h4 className="mb-0">My Pension Scheme Details</h4>
          <div className="text-muted small mt-1">
            
            Changes are notified to the pensions office automatically.
          </div>
        </Col>
        {ppsNumber && (
          <Col xs="auto" className="text-end">
            <Badge bg="secondary" style={{ fontFamily: 'monospace', fontSize: '.8rem', padding: '6px 10px' }}>
              {ppsNumber}
            </Badge>
            <div className="text-muted mt-1" style={{ fontSize: '.72rem' }}>PPS Number</div>
          </Col>
        )}
      </Row>

      {feedback && (
        <Alert variant={feedback.type} dismissible onClose={() => setFeedback(null)} className="mb-4">
          {feedback.msg}
        </Alert>
      )}

      <Form onSubmit={handleSave}>

        {/* ── Name ── */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <div style={SECTION_LABEL}>Your Name</div>
            <Form.Group>
              <Form.Label className="small fw-semibold">
                Full name <span className="text-muted fw-normal">(as it should appear on your statement)</span>
              </Form.Label>
              <Form.Control value={form.employeeName} onChange={(e) => upd('employeeName', e.target.value)} placeholder="Enter new name" />
            </Form.Group>
          </Card.Body>
        </Card>

        {/* ── Home address ── */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <div style={SECTION_LABEL}>Home Address</div>
            <Row className="g-2">
              {[['addressLine1','Address line 1'],['addressLine2','Address line 2'],['addressLine3','Address line 3 / Town'],['addressLine4','Address line 4 / County']].map(([field, label]) => (
                <Col xs={12} sm={6} key={field}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">{label}</Form.Label>
                    <Form.Control value={form[field]} onChange={(e) => upd(field, e.target.value)} />
                  </Form.Group>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        {/* ── Personal details ── */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <div style={SECTION_LABEL}>Personal Details</div>
            <Row className="g-3">
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Civil Status</Form.Label>
                  <Form.Select value={form.civilStatus} onChange={(e) => upd('civilStatus', e.target.value)}>
                    {CIVIL_OPTIONS.map((o) => <option key={o} value={o}>{o || '— Select to update —'}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Date of Birth</Form.Label>
                  <Form.Control type="date" value={form.dateOfBirth} onChange={(e) => upd('dateOfBirth', e.target.value)} max={new Date().toISOString().slice(0, 10)} />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ── Email address ── */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <div style={SECTION_LABEL}>Email Address</div>
            <Row className="align-items-center">
              <Col>
                <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 600 }}>
                  {userEmail || <span className="text-muted fst-italic fw-normal">No email address on file</span>}
                </div>
                <div className="text-muted small mt-1">
                  This is the address your Annual Benefit Statement will be sent to.
                  Changing it requires verifying access to the new address.
                </div>
              </Col>
              <Col xs="auto">
                <Button variant="outline-primary" size="sm" onClick={openEmailModal}>Change email</Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ── Password ── */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <div style={SECTION_LABEL}>Password</div>
            <Row className="align-items-center">
              <Col>
                <div className="text-muted small">A verification code will be sent to your email address before you can set a new password.</div>
              </Col>
              <Col xs="auto">
                <Button variant="outline-secondary" size="sm" onClick={openPwModal}>Change password</Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ── Scheme membership query ── */}
        <Card className={`mb-4 shadow-sm border-${schemeQueryRaised ? 'warning' : 'light'}`}>
          <Card.Body>
            <div style={{ ...SECTION_LABEL, color: schemeQueryRaised ? '#856404' : '#6c757d' }}>
              Pension Scheme Membership Query
            </div>
            <Row className="align-items-start g-3">
              <Col>
                <div className="small">
                  If you believe you should <strong>not</strong> be included in the Single Public
                  Service Pension Scheme — for example, because you are already a member of another
                  pension arrangement — use this toggle to flag your record for review by the
                  pensions office.
                </div>
                {schemeQueryRaised && (
                  <div className="mt-2 small text-warning fw-semibold">
                    ⚠ This query will be raised with the pensions office when you click Save changes below.
                    Toggle off to withdraw before saving.
                  </div>
                )}
              </Col>
              <Col xs={12} sm="auto" className="d-flex align-items-center gap-2">
                <Form.Check
                  type="switch"
                  id="scheme-query-switch"
                  label={schemeQueryRaised ? 'Query raised' : 'Raise a query'}
                  checked={schemeQueryRaised}
                  onChange={() => setSchemeQueryRaised((v) => !v)}
                  className={schemeQueryRaised ? 'text-warning fw-semibold' : 'text-muted'}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ── Save button — bottom of form ── */}
        <div className="d-flex align-items-center gap-3 mb-5">
          <Button type="submit" variant="primary" disabled={saving || raisingQuery}>
            {(saving || raisingQuery)
              ? <><Spinner size="sm" animation="border" className="me-1" />Saving…</>
              : 'Save changes'}
          </Button>
          <span className="text-muted small">
            The pensions office is notified automatically.
          </span>
        </div>

      </Form>

      {/* ═══════════════════ EMAIL CHANGE MODAL ═══════════════════════ */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Change email address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {emailFeedback && <Alert variant={emailFeedback.type} className="mb-3 py-2" style={{ fontSize: '.85rem' }}>{emailFeedback.msg}</Alert>}
          {emailStep === 'input' && (
            <>
              <p className="text-muted small mb-3">Enter your new email address. A 6-digit code will be sent to it to confirm you have access before any change is made.</p>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">New email address</Form.Label>
                <Form.Control type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="newaddress@example.com" autoComplete="email" autoFocus />
              </Form.Group>
              <Form.Group className="mb-1">
                <Form.Label className="small fw-semibold">Confirm new email address</Form.Label>
                <Form.Control type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder="Repeat the new address" autoComplete="email" />
              </Form.Group>
            </>
          )}
          {emailStep === 'verify' && (
            <>
              <p className="text-muted small mb-3">Check your inbox at <strong>{newEmail}</strong> for a 6-digit code. It expires in 10 minutes.</p>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Verification code</Form.Label>
                <Form.Control type="text" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="6-digit code" autoFocus style={{ fontFamily: 'monospace', letterSpacing: '0.3em', fontSize: '1.2rem', textAlign: 'center' }} />
              </Form.Group>
              <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={() => { setEmailStep('input'); setEmailFeedback(null); setOtp(''); }}>← Use a different address</button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowEmailModal(false)}>Cancel</Button>
          {emailStep === 'input' && (
            <Button variant="primary" size="sm" onClick={handleSendCode} disabled={sendingCode}>
              {sendingCode ? <><Spinner size="sm" animation="border" className="me-1" />Sending…</> : 'Send verification code'}
            </Button>
          )}
          {emailStep === 'verify' && (
            <Button variant="success" size="sm" onClick={handleConfirmEmail} disabled={confirming || otp.length < 6}>
              {confirming ? <><Spinner size="sm" animation="border" className="me-1" />Confirming…</> : 'Confirm email change'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════ PASSWORD CHANGE MODAL ════════════════════ */}
      <Modal show={showPwModal} onHide={() => setShowPwModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Change password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pwFeedback && <Alert variant={pwFeedback.type} className="mb-3 py-2" style={{ fontSize: '.85rem' }}>{pwFeedback.msg}</Alert>}
          {pwStep === 'send' && (
            <p className="text-muted small mb-0">A 6-digit verification code will be sent to <strong>{userEmail}</strong>. Once confirmed you can set your new password.</p>
          )}
          {pwStep === 'verify' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Verification code</Form.Label>
                <Form.Control type="text" inputMode="numeric" maxLength={6} value={pwOtp} onChange={(e) => setPwOtp(e.target.value.replace(/\D/g, ''))} placeholder="6-digit code" autoFocus style={{ fontFamily: 'monospace', letterSpacing: '0.3em', fontSize: '1.2rem', textAlign: 'center' }} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-semibold">New password</Form.Label>
                <div className="input-group">
                  <Form.Control type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
                  <Button variant="outline-secondary" size="sm" onClick={() => setShowNewPw((v) => !v)} tabIndex={-1} style={{ fontSize: '.75rem' }}>{showNewPw ? 'Hide' : 'Show'}</Button>
                </div>
                {pwStrength && (
                  <div className="mt-1">
                    <div className="progress" style={{ height: 4 }}>
                      <div className={`progress-bar bg-${pwStrength.variant}`} style={{ width: pwStrength.width, transition: 'width 0.3s' }} />
                    </div>
                    <div className={`text-${pwStrength.variant} mt-1`} style={{ fontSize: '.72rem' }}>{pwStrength.label}</div>
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-1">
                <Form.Label className="small fw-semibold">Confirm new password</Form.Label>
                <Form.Control type={showNewPw ? 'text' : 'password'} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Repeat new password" autoComplete="new-password" isInvalid={confirmPw.length > 0 && confirmPw !== newPassword} isValid={confirmPw.length > 0 && confirmPw === newPassword} />
                <Form.Control.Feedback type="invalid" style={{ fontSize: '.75rem' }}>Passwords do not match.</Form.Control.Feedback>
              </Form.Group>
              <button type="button" className="btn btn-link btn-sm p-0 text-muted mt-2" onClick={() => { setPwStep('send'); setPwFeedback(null); setPwOtp(''); }}>← Resend code</button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowPwModal(false)}>Cancel</Button>
          {pwStep === 'send' && (
            <Button variant="primary" size="sm" onClick={handleSendPwOtp} disabled={sendingPwOtp}>
              {sendingPwOtp ? <><Spinner size="sm" animation="border" className="me-1" />Sending…</> : 'Send code to my email'}
            </Button>
          )}
          {pwStep === 'verify' && (
            <Button variant="success" size="sm" onClick={handleChangePassword} disabled={changingPw || pwOtp.length < 6 || !newPassword || newPassword !== confirmPw}>
              {changingPw ? <><Spinner size="sm" animation="border" className="me-1" />Updating…</> : 'Update password'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
